(() => {
  const MOBILE_QUERY = '(max-width: 990px)';

  const init = () => {
    const section = document.querySelector('[data-iurop-collection]');
    if (!section) return;

    const sidebar = section.querySelector('[data-filters]');
    const toggle = section.querySelector('[data-filters-toggle]');
    const form = section.querySelector('[data-filters-form]');
    const clear = section.querySelector('[data-filters-clear]');
    const closeBtn = section.querySelector('[data-filters-close]');
    const backdrop = section.querySelector('[data-filters-backdrop]');
    const content = section.querySelector('.iurop-collection__content');

    const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

    const setExpanded = (open) => {
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    };

    const lockScroll = (lock) => {
      document.documentElement.style.overflow = lock ? 'hidden' : '';
      document.body.style.overflow = lock ? 'hidden' : '';
    };

    const openMobile = () => {
      if (!sidebar) return;
      sidebar.classList.add('is-open');
      sidebar.classList.remove('is-collapsed');
      if (backdrop) {
        backdrop.hidden = false;
        // Force reflow to enable transition from hidden state.
        // eslint-disable-next-line no-unused-expressions
        backdrop.offsetWidth;
        backdrop.classList.add('is-open');
      }
      lockScroll(true);
      document.body.classList.add('iurop-filters-open');
      setExpanded(true);
    };

    const closeMobile = () => {
      if (!sidebar) return;
      sidebar.classList.remove('is-open');
      if (backdrop) {
        backdrop.classList.remove('is-open');
        const onEnd = () => {
          backdrop.hidden = true;
          backdrop.removeEventListener('transitionend', onEnd);
        };
        backdrop.addEventListener('transitionend', onEnd);
      }
      lockScroll(false);
      document.body.classList.remove('iurop-filters-open');
      setExpanded(false);
    };

    const toggleDesktop = () => {
      if (!sidebar) return;
      const collapsed = sidebar.classList.toggle('is-collapsed');
      setExpanded(!collapsed);
    };

    // Initial state per viewport.
    const applyInitialState = () => {
      if (!sidebar) return;
      sidebar.classList.remove('is-open');
      if (backdrop) {
        backdrop.classList.remove('is-open');
        backdrop.hidden = true;
      }
      lockScroll(false);
      document.body.classList.remove('iurop-filters-open');
      if (isMobile()) {
        sidebar.classList.remove('is-collapsed');
        setExpanded(false);
      } else {
        sidebar.classList.remove('is-collapsed');
        setExpanded(true);
      }
    };

    applyInitialState();

    if (toggle) {
      toggle.addEventListener('click', () => {
        if (isMobile()) {
          if (sidebar.classList.contains('is-open')) closeMobile();
          else openMobile();
        } else {
          toggleDesktop();
        }
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closeMobile);
    }

    if (backdrop) {
      backdrop.addEventListener('click', closeMobile);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isMobile() && sidebar.classList.contains('is-open')) {
        closeMobile();
      }
    });

    // Reset state on resize across breakpoint.
    let wasMobile = isMobile();
    window.addEventListener('resize', () => {
      const nowMobile = isMobile();
      if (nowMobile !== wasMobile) {
        wasMobile = nowMobile;
        applyInitialState();
      }
    });

    // ----- AJAX filtering -----
    let inflight = null;

    const buildUrl = () => {
      const params = new URLSearchParams();
      const data = new FormData(form);
      for (const [key, value] of data.entries()) {
        if (value === '' || value == null) continue;
        params.append(key, value);
      }
      const qs = params.toString();
      return `${window.location.pathname}${qs ? `?${qs}` : ''}`;
    };

    const applyFromUrl = (target, { push = true } = {}) => {
      if (!form || !content) return;
      if (inflight) inflight.abort();
      const ctrl = new AbortController();
      inflight = ctrl;
      section.classList.add('is-filtering');

      return fetch(target, { signal: ctrl.signal })
        .then((res) => res.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newForm = doc.querySelector('[data-filters-form]');
          const newContent = doc.querySelector('.iurop-collection__content');
          if (newForm) form.innerHTML = newForm.innerHTML;
          if (newContent) content.innerHTML = newContent.innerHTML;
          if (push) window.history.pushState({ iuropCollection: true }, '', target);
          setupInfiniteScroll();
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          // Fall back to a hard navigation if the fetch fails so the user
          // still sees the filtered results.
          console.error('[iurop-collection] filter fetch failed', err);
          window.location.assign(target);
        })
        .finally(() => {
          if (inflight === ctrl) inflight = null;
          section.classList.remove('is-filtering');
        });
    };

    // ----- Infinite scroll (desktop only) -----
    // Loads the next paginated page when a sentinel below the grid enters the
    // viewport, then appends its product cards in place. The numbered
    // pagination remains in the DOM (hidden on desktop via CSS) so Googlebot
    // can still crawl every page individually.
    let infiniteObserver = null;
    let infiniteLoading = false;

    const setupInfiniteScroll = () => {
      if (infiniteObserver) {
        infiniteObserver.disconnect();
        infiniteObserver = null;
      }
      if (!content) return;

      const sentinel = content.querySelector('[data-infinite-sentinel]');
      const grid = content.querySelector('[data-collection-grid]');
      if (!sentinel || !grid) return;

      infiniteObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            loadNextPage(sentinel, grid);
          });
        },
        { rootMargin: '400px 0px' }
      );
      infiniteObserver.observe(sentinel);
    };

    const loadNextPage = (sentinel, grid) => {
      if (infiniteLoading) return;
      const nextUrl = sentinel.getAttribute('data-next-url');
      if (!nextUrl) return;
      infiniteLoading = true;
      sentinel.classList.add('is-loading');

      fetch(nextUrl)
        .then((res) => res.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newGrid = doc.querySelector('[data-collection-grid]');
          if (newGrid) {
            const frag = document.createDocumentFragment();
            Array.from(newGrid.children).forEach((node) => frag.appendChild(node));
            grid.appendChild(frag);
          }

          const newSentinel = doc.querySelector('[data-infinite-sentinel]');
          if (newSentinel) {
            sentinel.setAttribute('data-next-url', newSentinel.getAttribute('data-next-url') || '');
            sentinel.setAttribute('data-current-page', newSentinel.getAttribute('data-current-page') || '');
          } else {
            // No more pages — remove the sentinel and stop observing.
            if (infiniteObserver) infiniteObserver.disconnect();
            sentinel.remove();
          }

          // NOTE: we deliberately do NOT call history.replaceState() here.
          // Writing to the history on every appended page while the user is
          // scrolling froze momentum scrolling on iOS Safari (content stopped
          // repainting — "se queda a medias, no puedo subir") and pointed the
          // URL at ?page=N, so going back lost the earlier products. Infinite
          // scroll keeps the URL on the first page, which is the expected
          // behaviour and keeps every page crawlable via the sentinel/noscript.
        })
        .catch((err) => {
          console.error('[iurop-collection] infinite scroll fetch failed', err);
        })
        .finally(() => {
          infiniteLoading = false;
          sentinel.classList.remove('is-loading');
        });
    };

    setupInfiniteScroll();
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setupInfiniteScroll, 150);
    });

    const submit = () => {
      applyFromUrl(buildUrl());
    };

    if (form) {
      form.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (target.matches('input[type="checkbox"], input[type="radio"], input[type="number"]')) {
          submit();
        }
      });

      form.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        const target = event.target;
        if (target instanceof HTMLElement && target.matches('input[type="number"]')) {
          event.preventDefault();
          submit();
        }
      });

      // Prevent native submit (e.g. when pressing Enter on a non-number field).
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submit();
      });
    }

    if (clear) {
      clear.addEventListener('click', (event) => {
        event.preventDefault();
        const href = clear.getAttribute('href') || window.location.pathname;
        applyFromUrl(href);
      });
    }

    window.addEventListener('popstate', () => {
      applyFromUrl(`${window.location.pathname}${window.location.search}`, { push: false });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
